/** Form snapshots stay in memory. Never persist these values in browser storage. */
export function formSnapshot(value: unknown): string {
  return JSON.stringify(value, (_key, current) => {
    if (current && typeof current === 'object' && !Array.isArray(current)) {
      return Object.keys(current).sort().reduce<Record<string, unknown>>((result, key) => {
        result[key] = current[key]
        return result
      }, {})
    }
    return current
  }) ?? ''
}

export function hasFormChanges(baseline: unknown, current: unknown): boolean {
  return formSnapshot(baseline) !== formSnapshot(current)
}

export interface UnsavedFormState {
  dirty: boolean
  busy?: boolean
}

export function needsNavigationWarning(forms: Iterable<UnsavedFormState>): boolean {
  return Array.from(forms).some((form) => form.dirty || form.busy)
}

export function canDiscardChanges(forms: Iterable<UnsavedFormState>): boolean {
  return !Array.from(forms).some((form) => form.busy)
}

export function dialogClosePolicy(form: UnsavedFormState): 'close' | 'confirm' | 'wait' {
  if (form.busy) return 'wait'
  return form.dirty ? 'confirm' : 'close'
}

export interface GuardState extends UnsavedFormState {
  onDiscard?: () => void
}

export interface GuardRegistration {
  current: () => GuardState
  release: () => void
}

/** Shared by dialog dismissal and router/history transitions. Values remain in memory. */
export class UnsavedChangesRegistry {
  private readonly registrations = new Map<string, GuardRegistration>()

  register(id: string, registration: GuardRegistration): () => void {
    this.registrations.set(id, registration)
    return () => {
      // An old editor's cleanup must not remove a newer registration for that id.
      if (this.registrations.get(id) === registration) this.registrations.delete(id)
    }
  }

  has(id: string): boolean {
    return this.registrations.has(id)
  }

  entries(id?: string): [string, GuardRegistration][] {
    if (id !== undefined) {
      const entry = this.registrations.get(id)
      return entry ? [[id, entry]] : []
    }
    return Array.from(this.registrations.entries())
  }

  states(id?: string): GuardState[] {
    return this.entries(id).map(([, entry]) => entry.current())
  }

  needsWarning(id?: string): boolean {
    return needsNavigationWarning(this.states(id))
  }

  canDiscard(id?: string): boolean {
    return canDiscardChanges(this.states(id))
  }

  release(id: string): void {
    this.registrations.get(id)?.release()
    this.registrations.delete(id)
  }

  discardAndContinue(action: () => void, id?: string): boolean {
    if (!this.discard(id)) return false
    action()
    return true
  }

  discard(id?: string): boolean {
    const entries = this.entries(id)
    if (!canDiscardChanges(entries.map(([, entry]) => entry.current()))) return false
    for (const [key, entry] of entries) {
      const state = entry.current()
      if (state.dirty) state.onDiscard?.()
      this.release(key)
    }
    return true
  }
}
